import { readInventorizationIdentity } from '@/features/inventorization/domain/inventorization-identity';
import { cacheId, toMeterSearchData } from '@/features/location-bootstrap/location-bootstrap-cache-records';
import type { LocationBootstrapPayload } from '@/features/location-bootstrap/location-bootstrap-types';
import { CAPTURE_SESSION_STATUSES } from '@/shared/capture/capture-session';
import type { FieldDatabase } from '@/shared/db/schema';
import { normalizeSearchField } from '@/shared/meter-identification';

export async function putBootstrapMirrors(database: FieldDatabase, payload: LocationBootstrapPayload) {
  await Promise.all([putMeters(database, payload), linkSubmittedLocalSessions(database, payload)]);
}

async function putMeters(database: FieldDatabase, payload: LocationBootstrapPayload) {
  await Promise.all(
    payload.existingMeters.map(meter =>
      database.put('meters', {
        data: toMeterSearchData(payload, meter),
        id: cacheId(payload.locationId, 'meter', meter._id),
        remoteId: meter._id,
        updatedAt: meter.updatedAt ?? payload.loadedAt,
      }),
    ),
  );
}

async function linkSubmittedLocalSessions(database: FieldDatabase, payload: LocationBootstrapPayload) {
  for (const session of await database.getAll('capture_sessions')) {
    if (session._id) continue;
    if (session.locationId != null && String(session.locationId) !== String(payload.locationId)) continue;
    const submitted =
      session.status === CAPTURE_SESSION_STATUSES.synced || session.status === CAPTURE_SESSION_STATUSES.needsReview;
    if (!(session.meterId || submitted)) continue;

    const identity = readInventorizationIdentity(session);
    const apiKey = normalizeSearchField(identity.apiKey);
    const serial = normalizeSearchField(identity.serial);
    const matches = payload.existingMeters.filter(meter => {
      if (session.meterId) return meter.id === session.meterId;
      if (apiKey) return normalizeSearchField(meter.apiKey) === apiKey;
      return serial && normalizeSearchField(meter.serial) === serial && meter.commodity === identity.commodity;
    });
    if (matches.length !== 1) continue;
    const meter = matches[0];
    if (!(meter?._id && meter.id)) continue;

    await database.put('capture_sessions', {
      ...session,
      _id: meter._id,
      ...(session.locationId == null && Number.isFinite(Number(payload.locationId))
        ? { locationId: Number(payload.locationId) }
        : {}),
      meterId: meter.id,
      updatedAt: meter.updatedAt ?? payload.loadedAt,
    });
  }
}
